import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AutoComplete, Button, Input, Select, message } from "antd";
import { getContactModes, updateContactMode } from "../../../services/polly/llmSettingsService";
import { searchCustomers } from "../../../services/polly/customerSearchService";
import { CHANNEL_LLM_MODES, CHANNEL_LLM_MODE_OPTIONS } from "../../../constants/Polly/ChannelLlmModes";
import { CONTACT_LLM_MODES } from "../../../constants/Polly/ContactLlmModes";

const { Option } = Select;
const { ENABLED, DISABLED, INHERIT } = CONTACT_LLM_MODES;
const SEARCH_DEBOUNCE_MS = 300;

export const customerName = (customer) =>
  [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || `Customer ${customer?.contactId}`;

// Phone and email tell apart customers who share a name in the search results.
const contactDetails = (customer) =>
  [customer.primaryPhoneNumber, customer.primaryEmailAddress].filter(Boolean).join(" · ");

const channelModeLabel = (mode) =>
  CHANNEL_LLM_MODE_OPTIONS.find((opt) => opt.value === mode)?.label || mode;

// Which list a client acts on under each channel mode. The other list is stored but changes
// nothing until the mode changes, so it is greyed rather than hidden.
const layoutFor = (channelMode) => {
  if (channelMode === CHANNEL_LLM_MODES.ENABLED_SPECIFIC_CONTACTS) {
    return { lead: ENABLED, other: DISABLED, otherInert: true, addDefault: ENABLED };
  }
  if (channelMode === CHANNEL_LLM_MODES.ENABLED_ALL) {
    return { lead: DISABLED, other: ENABLED, otherInert: true, addDefault: DISABLED };
  }
  return { lead: ENABLED, other: DISABLED, otherInert: false, addDefault: ENABLED };
};

/**
 * One channel's customer overrides on Polly › Settings: the stored exceptions, a reset per
 * customer, and a search to add one customer at a time.
 */
export default function ChannelOverrides({ channel, customerArn, customerId, locked = false }) {
  const { channelType, displayName, llmMode: channelMode } = channel;
  const layout = layoutFor(channelMode);

  const [modes, setModes] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [addMode, setAddMode] = useState(layout.addDefault);
  const searchTimer = useRef(null);
  const latestQuery = useRef("");

  useEffect(() => {
    setAddMode(layoutFor(channelMode).addDefault);
  }, [channelMode]);

  const load = useCallback(async () => {
    try {
      const res = await getContactModes(channelType);
      setModes(Array.isArray(res) ? res : []);
      setLoadError(null);
    } catch (err) {
      setModes([]);
      setLoadError(err?.response?.status ?? 0);
    }
  }, [channelType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const write = async (customer, mode, successText) => {
    setBusy(true);
    try {
      await updateContactMode(customer.contactId, channelType, mode);
      message.success(successText);
      await load();
      return true;
    } catch (err) {
      message.error(err?.response?.data?.error || `Failed to update ${customerName(customer)}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSearch = (text) => {
    setQuery(text);
    setPicked(null);
    latestQuery.current = text.trim();
    clearTimeout(searchTimer.current);
    if (!latestQuery.current) {
      setOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      const asked = latestQuery.current;
      try {
        const { customers } = await searchCustomers(customerArn, { query: asked, page: 0 });
        // A slower earlier response must not replace the results for what is typed now.
        if (asked !== latestQuery.current) return;
        setOptions(customers.map((c) => ({
          value: String(c.contactId),
          label: (
            <span>
              {customerName(c)}
              {contactDetails(c) && <span className="llm-overrides-option-detail">{` · ${contactDetails(c)}`}</span>}
            </span>
          ),
          customer: c,
        })));
      } catch (err) {
        setOptions([]);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const onSelect = (value, option) => {
    setPicked(option.customer);
    setQuery(customerName(option.customer));
  };

  const onAdd = async () => {
    if (!picked) return;
    const verb = addMode === ENABLED ? "enabled" : "disabled";
    const ok = await write(picked, addMode, `${customerName(picked)} ${verb} on ${displayName}`);
    if (ok) {
      setPicked(null);
      setQuery("");
      setOptions([]);
    }
  };

  const rowsFor = (mode) => modes.filter((row) => row.llmMode === mode);

  const emptyTextFor = (mode) => {
    if (mode === DISABLED) return "No customers are disabled individually.";
    return channelMode === CHANNEL_LLM_MODES.ENABLED_SPECIFIC_CONTACTS
      ? "No customers are enabled yet, so this channel reaches nobody."
      : "No customers are enabled individually.";
  };

  const renderList = (mode, inert) => {
    const rows = rowsFor(mode);
    const title = mode === ENABLED ? "Enabled" : "Disabled";
    return (
      <div className={`llm-overrides-list${inert ? " llm-overrides-inert" : ""}`} data-list={mode}>
        <div className="llm-settings-label">
          {rows.length > 0 ? `${title} (${rows.length})` : title}
          {inert && (
            <span className="llm-overrides-why">
              {`No effect while this channel is "${channelModeLabel(channelMode)}"`}
            </span>
          )}
        </div>
        {rows.length === 0 && <div className="llm-settings-empty">{emptyTextFor(mode)}</div>}
        {rows.length > 0 && (
          <div className="llm-settings-chip-list">
            {rows.map((row) => (
              // Same chip as the term lists further down the page, with the name as a link.
              <span key={row.contactId} className="llm-settings-chip">
                <Link
                  to={{
                    pathname: "/contacts/contact-detail",
                    // The detail page loads attributes and products by the client's customerId.
                    state: {
                      userToEdit: { customerId, id: row.contactId, firstName: row.firstName, lastName: row.lastName },
                    },
                  }}
                >
                  {customerName(row)}
                </Link>
                {!locked && (
                  <button
                    type="button"
                    className="llm-settings-chip-remove"
                    aria-label={`Reset ${customerName(row)}`}
                    disabled={busy}
                    onClick={() => write(row, INHERIT, `${customerName(row)} now follows the channel setting`)}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loadError !== null) {
    return (
      <div className="llm-overrides">
        <div className="llm-settings-empty">
          {loadError === 403
            ? "Not available while LLM conversations are turned off for your account."
            : "These overrides could not be loaded. Try again shortly."}
        </div>
      </div>
    );
  }

  const channelOff = channelMode === CHANNEL_LLM_MODES.DISABLED;

  if (channelOff && !expanded) {
    const count = modes.length;
    return (
      <div className="llm-overrides">
        <span className="llm-settings-hint">
          {`Polly doesn’t reply on ${displayName} while it’s off.`}
          {count > 0 && ` ${count} customer ${count === 1 ? "override is" : "overrides are"} kept for when it’s turned on.`}
        </span>
        {count > 0 && (
          <Button type="link" size="small" onClick={() => setExpanded(true)}>Show</Button>
        )}
      </div>
    );
  }

  return (
    <div className="llm-overrides">
      {renderList(layout.lead, false)}
      {renderList(layout.other, layout.otherInert)}
      {!channelOff && (
        <div className="llm-settings-add-row llm-overrides-add">
          <AutoComplete
            value={query}
            options={options}
            onSearch={onSearch}
            onSelect={onSelect}
            disabled={locked || busy}
            style={{ width: 300 }}
          >
            {/* A real input carries the placeholder; AutoComplete's own is a decorative span. */}
            <Input placeholder="Find a customer…" disabled={locked || busy} />
          </AutoComplete>
          <Select value={addMode} onChange={setAddMode} disabled={locked || busy} style={{ width: 120 }}>
            <Option value={ENABLED}>Enabled</Option>
            <Option value={DISABLED}>Disabled</Option>
          </Select>
          <Button onClick={onAdd} disabled={!picked || locked || busy} loading={busy}>Add</Button>
        </div>
      )}
    </div>
  );
}
